import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { subirArchivo, abrirArchivoSeguro } from "../utils/upload";
import {
  BadgeCredito,
  BANCOS_PAGO,
  DesglosePago,
  METODOS_PAGO,
  TablaItemsPago,
  DocumentosRespaldo,
  fechaCorta,
  folioPago,
  montoTextoPago,
  netoTextoPago,
  nombreMetodo,
  textoArticulos,
  textoPlazo,
} from "../utils/pagosUi";
import { EncabezadoDetalle, tarjetaTema, tituloSeccionTema } from "../components/TemaDetalle";
import Cargando from "../components/Cargando";

const hoy = () => new Date().toISOString().slice(0, 10);

const CAMPOS = {
  cheque: { referencia: "Número de cheque", requerida: true, banco: true, comprobante: "opcional" },
  transferencia: { referencia: "Número de referencia", requerida: true, banco: true, comprobante: "obligatorio" },
  efectivo: { referencia: null, banco: false, comprobante: "opcional" },
  tarjeta: { referencia: "Referencia o autorización (opcional)", requerida: false, banco: false, comprobante: "obligatorio" },
};

// Registrar el pago de una factura a crédito (o ver el detalle si ya se pagó).
function RegistrarCredito() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const puedePagar = usuario?.rol === "pagos" || usuario?.rol === "administrador";

  const [credito, setCredito] = useState(null);
  const [error, setError] = useState("");
  const [metodo, setMetodo] = useState("transferencia");
  const [referencia, setReferencia] = useState("");
  const [banco, setBanco] = useState("");
  const [fecha, setFecha] = useState(hoy());
  const [observaciones, setObservaciones] = useState("");
  const [comprobante, setComprobante] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [recienPagado, setRecienPagado] = useState(false);
  const archivoRef = useRef(null);

  useEffect(() => {
    apiFetch(`/api/pagos/creditos/${id}`)
      .then(setCredito)
      .catch((err) => setError(err.message));
  }, [id]);

  if (error && !credito) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!credito) return <Cargando />;

  const pagado = credito.fase_credito === "pagado";
  const campos = CAMPOS[metodo];
  const items = credito.items || [];

  const subirComprobante = async (file) => {
    if (!file) return;
    setSubiendo(true);
    setError("");
    try {
      setComprobante(await subirArchivo(file));
    } catch (err) {
      setError(err.message);
    } finally {
      setSubiendo(false);
    }
  };

  const registrar = async () => {
    if (campos.requerida && !referencia.trim()) return setError(`Escribe el ${campos.referencia.toLowerCase()}.`);
    if (campos.comprobante === "obligatorio" && !comprobante) return setError("Adjunta el comprobante del pago.");
    setGuardando(true);
    setError("");
    try {
      await apiFetch(`/api/pagos/creditos/${credito.id}/pagar`, {
        method: "POST",
        body: JSON.stringify({
          metodo,
          referencia: referencia.trim(),
          banco: banco.trim(),
          fecha_pago: fecha,
          observaciones: observaciones.trim(),
          comprobante,
        }),
      });
      setCredito(await apiFetch(`/api/pagos/creditos/${credito.id}`));
      setRecienPagado(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  // Si se abrió desde otra pantalla (p. ej. el detalle de la solicitud), se regresa a ella;
  // si no, a la lista de facturas a crédito.
  const destinoRegreso = location.state?.desde || "/pagos/creditos";

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Link to={destinoRegreso} style={styles.volver}>
        {pagado || location.state?.desde ? "← Regresar" : "← Regresar a facturas a crédito"}
      </Link>
      <EncabezadoDetalle
        titulo={pagado ? "✓ Factura a crédito pagada" : "🗓️ Pagar factura a crédito"}
        enlace={`/solicitud/${credito.solicitud_id}`}
        enlaceTexto={`Solicitud ${folioPago(credito.solicitud_id)}`}
        insignia={<BadgeCredito fase={credito.fase_credito} />}
        subtitulo={`${credito.proveedor_nombre} · ${textoArticulos(items)}`}
        etiquetaMonto={pagado ? "Pagado" : "Por pagar"}
        monto={netoTextoPago(credito) || montoTextoPago(credito)}
        notaMonto={netoTextoPago(credito) ? `Factura: ${montoTextoPago(credito)}` : null}
        tono={pagado ? "pagado" : credito.fase_credito === "vencido" ? "vencido" : "pendiente"}
      />

      {recienPagado && <div style={styles.exito}>✓ Pago registrado. La factura quedó saldada.</div>}

      <div style={styles.card}>
        <div style={styles.datos}>
          <Dato etiqueta="Plazo" valor={`${credito.plazo_dias} días desde la recepción`} />
          <Dato
            etiqueta="Vence"
            valor={
              credito.vence_en ? (
                <>
                  {fechaCorta(credito.vence_en)}
                  {!pagado && <div style={{ ...styles.plazo, color: colorPlazo(credito), fontWeight: 700 }}>{textoPlazo(credito)}</div>}
                </>
              ) : (
                <span style={styles.plazo}>{textoPlazo(credito)}</span>
              )
            }
          />
          <Dato etiqueta="Solicitante" valor={`${credito.solicitante_nombre} · ${credito.area_nombre}`} />
          <Dato etiqueta="Aprobado por" valor={credito.aprobado_por_nombre || "—"} />
          <Dato
            etiqueta="Cotización"
            valor={
              credito.archivo_cotizacion ? (
                <a href="#" onClick={(e) => abrirArchivoSeguro(e, credito.archivo_cotizacion)} style={styles.link}>
                  📄 Ver cotización
                </a>
              ) : (
                "—"
              )
            }
          />
        </div>
      </div>

      {pagado ? (
        <div style={styles.card}>
          <div style={styles.tituloSeccion}>Detalle del pago</div>
          <div style={styles.datos}>
            <Dato etiqueta="Forma de pago" valor={nombreMetodo(credito.metodo)} />
            {credito.referencia && (
              <Dato etiqueta={credito.metodo === "cheque" ? "Número de cheque" : "Referencia"} valor={credito.referencia} />
            )}
            {credito.banco && <Dato etiqueta="Banco" valor={credito.banco} />}
            <Dato etiqueta="Fecha del pago" valor={fechaCorta(credito.fecha_pago)} />
            <Dato etiqueta="Registrado por" valor={credito.pagado_por_nombre || "—"} />
            <Dato
              etiqueta="Comprobante"
              valor={
                credito.comprobante ? (
                  <a href="#" onClick={(e) => abrirArchivoSeguro(e, credito.comprobante)} style={styles.link}>
                    🧾 Ver comprobante ✓
                  </a>
                ) : (
                  "Sin comprobante"
                )
              }
            />
          </div>
          {credito.observaciones && <p style={styles.observaciones}>Observaciones: {credito.observaciones}</p>}
          <div style={styles.acciones}>
            <button type="button" onClick={() => navigate(destinoRegreso)} style={styles.botonPrincipal}>
              Regresar
            </button>
          </div>
        </div>
      ) : !puedePagar ? (
        <div style={styles.card}>
          <p style={{ margin: 0, color: "#5b6472", fontSize: "0.88rem" }}>Solo quien paga puede registrar este pago.</p>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={styles.tituloSeccion}>Registrar el pago de la factura</div>

          <div style={styles.opciones}>
            {METODOS_PAGO.map((m) => (
              <label key={m.valor} style={{ ...styles.opcion, ...(metodo === m.valor ? styles.opcionActiva : {}) }}>
                <input type="radio" name="metodo" checked={metodo === m.valor} onChange={() => setMetodo(m.valor)} />
                {m.icono} {m.texto}
              </label>
            ))}
          </div>

          <div style={styles.formulario}>
            {campos.referencia && (
              <label style={styles.etiqueta}>
                {campos.referencia}
                {campos.requerida ? " *" : ""}
                <input type="text" value={referencia} onChange={(e) => setReferencia(e.target.value)} style={styles.input} />
              </label>
            )}
            {campos.banco && (
              <label style={styles.etiqueta}>
                Banco
                <select value={banco} onChange={(e) => setBanco(e.target.value)} style={styles.input}>
                  <option value="">Elegir banco…</option>
                  {BANCOS_PAGO.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label style={styles.etiqueta}>
              Fecha del pago
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />
            </label>
          </div>

          <label style={{ ...styles.etiqueta, marginTop: "0.9rem" }}>
            Observaciones (opcional)
            <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows={2} style={{ ...styles.input, resize: "vertical" }} />
          </label>

          <div style={{ marginTop: "0.9rem" }}>
            <input
              ref={archivoRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: "none" }}
              onChange={(e) => subirComprobante(e.target.files[0])}
            />
            <button type="button" onClick={() => archivoRef.current?.click()} style={styles.botonSecundario}>
              📎 {subiendo ? "Subiendo..." : comprobante ? "Comprobante adjuntado ✓" : "Adjuntar comprobante"}
              {!comprobante ? (campos.comprobante === "obligatorio" ? " (obligatorio)" : " (opcional)") : ""}
            </button>
          </div>

          {error && <p style={{ color: "#a3402f", fontSize: "0.85rem" }}>{error}</p>}

          <div style={styles.acciones}>
            <button type="button" onClick={() => navigate("/pagos/creditos")} style={styles.botonSecundario}>
              Cancelar
            </button>
            <button type="button" disabled={guardando || subiendo} onClick={registrar} style={styles.botonPrincipal}>
              {guardando ? "Guardando..." : "✓ Registrar pago"}
            </button>
          </div>
        </div>
      )}

      <DocumentosRespaldo datos={credito} />

      <div style={styles.card}>
        <div style={{ ...styles.tituloSeccion, marginBottom: "0.7rem" }}>Productos de la factura</div>
        <TablaItemsPago items={items} moneda={credito.moneda} />
        <DesglosePago pago={credito} />
      </div>
    </div>
  );
}

// Verde con tiempo, naranja si faltan 7 días o menos, rojo si ya venció.
function colorPlazo(c) {
  const d = Number(c.dias_restantes);
  if (d < 0) return "#a3402f";
  return d <= 7 ? "#b8722a" : "#2f7a54";
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <div style={styles.etiquetaDato}>{etiqueta}</div>
      <div style={styles.valorDato}>{valor}</div>
    </div>
  );
}

const styles = {
  volver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  exito: { marginTop: "0.9rem", padding: "0.7rem 1rem", borderRadius: 10, background: "#dcf0e6", color: "#1e5e3f", fontWeight: 600, fontSize: "0.9rem" },
  card: { border: "1px solid #e2e6ea", borderRadius: 14, padding: "1.1rem 1.3rem", marginTop: "1rem", background: "#fff", boxShadow: "0 1px 3px rgba(27,42,74,0.06)", ...tarjetaTema },
  datos: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "0.9rem 1.5rem", marginTop: "0.9rem" },
  etiquetaDato: { fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: "0.15rem" },
  valorDato: { fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, overflowWrap: "anywhere" },
  plazo: { fontSize: "0.76rem", color: "#8993a1", fontWeight: 500 },
  link: { color: "var(--tema-primario, #2f6690)", textDecoration: "none", fontWeight: 600 },
  tituloSeccion: tituloSeccionTema,
  opciones: { display: "flex", flexWrap: "wrap", gap: "0.5rem", margin: "0.8rem 0" },
  opcion: { display: "inline-flex", alignItems: "center", gap: "0.4rem", border: "1px solid #d3d9df", borderRadius: 8, padding: "0.45rem 0.8rem", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)", background: "#fff", cursor: "pointer" },
  opcionActiva: { borderColor: "#0f7c8a", background: "#e6f4f6", fontWeight: 700 },
  formulario: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem 1.2rem", marginTop: "0.4rem" },
  etiqueta: { display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.82rem", fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" },
  input: { padding: "0.5rem 0.6rem", border: "1px solid #d3d9df", borderRadius: 6, fontFamily: "inherit", fontSize: "0.88rem", boxSizing: "border-box", width: "100%" },
  observaciones: { margin: "0.9rem 0 0", fontSize: "0.86rem", color: "#3d4a5c" },
  acciones: { display: "flex", gap: "0.6rem", flexWrap: "wrap", justifyContent: "flex-end", marginTop: "1rem" },
  botonPrincipal: { background: "#0f7c8a", color: "#fff", border: "none", borderRadius: 8, padding: "0.6rem 1.2rem", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer" },
  botonSecundario: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #d3d9df", borderRadius: 8, padding: "0.55rem 1rem", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer" },
};

export default RegistrarCredito;
